import React, { useEffect, useState } from 'react';

import Modal from '../../modules/Modal';
import styles from '../Timeline.module.scss';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import {
  createVideofeedDownloadableLink,
  getPublicDownloadLinkExpiry,
  getVideofeedDownloadDataByVideoFeedId,
} from '../../../api';
import { enqueueSnackbar } from 'notistack';
import { useAppSelector } from '../../../store/hooks';
import { VIDEO_TYPE, VIDEOFEED, VIDEOFEED_TYPE } from '../../../enums';

type TCopyLinkModal = {
  open: boolean;
  switchModal: () => void;
  feedData: VIDEOFEED | null;
  composerQuality?: string;
  itsComposer?: boolean;
};

const CopyLinkModal: React.FC<TCopyLinkModal> = ({ open, switchModal, feedData, itsComposer }) => {
  const [loading, setLoading] = useState(false);
  const { currentVideoThatInFrame, selectedVideoQuality } = useAppSelector((state) => state.sharedStore);
  const [data, setData] = useState<{ session_name: any; angles: any } | null>(null);
  const [downloadFeed, setDownloadFeed] = useState<VIDEOFEED | null>(null);
  const [showDownloadableSelector, setShowDownloadableSelector] = useState(false);
  const [selectedDownloadableVideoId, setSelectedDownloadableVideoId] = useState<number | null>(null);
  const [downloadableLinksByVideoId, setDownloadableLinksByVideoId] = useState<Record<number, string>>({});
  const [ttlSeconds, setTtlSeconds] = useState(7 * 24 * 60 * 60);
  const canCopyDownloadableLink =
    feedData?.type !== VIDEOFEED_TYPE.MY_COMPOSER;

  const fetchAndParse = async () => {
    if (!feedData) return;
    try {
      setLoading(true);

      const json = {
        session_name: feedData.title,
        angles: feedData.videos.map((video: VIDEO_TYPE) => ({
          angle_id: video?.angle, // as requested, using video.angle
          // You can adjust the naming logic as needed:
          angle_name: video?.name || video?.angle,
          // Use the selected quality to pick the correct URL from s3_keys:
          angle_m3u8_url: video.s3_keys[selectedVideoQuality!.quality],
        })),
      };
      setData(json);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      }

      document.body.removeChild(textarea);
      return copied;
    }
  };

  const showManualCopyPrompt = (value: string) => {
    window.prompt('Copy link manually:', value);
  };

  useEffect(() => {
    if (feedData && selectedVideoQuality?.quality) {
      fetchAndParse();
    }
  }, [feedData, selectedVideoQuality]);

  useEffect(() => {
    if (!open || !feedData?.id) {
      setDownloadFeed(null);
      return;
    }

    const loadFeed = async () => {
      try {
        const [res, expiry] = await Promise.all([
          getVideofeedDownloadDataByVideoFeedId(String(feedData.id)),
          getPublicDownloadLinkExpiry().catch(() => ({ ttlSeconds: 7 * 24 * 60 * 60 })),
        ]);
        setDownloadFeed((res?.data || null) as VIDEOFEED | null);
        setTtlSeconds(Number(expiry?.ttlSeconds) || 7 * 24 * 60 * 60);
      } catch (error) {
        console.error(error);
        setDownloadFeed(null);
      }
    };

    void loadFeed();
  }, [open, feedData?.id]);

  const videosForDownload = React.useMemo(() => {
    const baseVideos = feedData?.videos || [];
    if (!downloadFeed?.videos?.length) {
      return baseVideos;
    }

    const byId = new Map(
      downloadFeed.videos.map((video) => [Number(video.id), video]),
    );

    return baseVideos
      .map((video) => byId.get(Number(video.id)) || video)
      .filter(Boolean);
  }, [downloadFeed?.videos, feedData?.videos]);

  useEffect(() => {
    if (!open) {
      setShowDownloadableSelector(false);
      setSelectedDownloadableVideoId(null);
      setDownloadableLinksByVideoId({});
      return;
    }

    const fallbackVideoId =
      currentVideoThatInFrame?.id || videosForDownload?.[0]?.id || null;
    setSelectedDownloadableVideoId(
      fallbackVideoId ? Number(fallbackVideoId) : null,
    );
  }, [open, currentVideoThatInFrame?.id, videosForDownload]);

  useEffect(() => {
    setDownloadableLinksByVideoId({});
  }, [ttlSeconds]);

  useEffect(() => {
    if (!showDownloadableSelector || !feedData?.id || !canCopyDownloadableLink || !videosForDownload.length) {
      return;
    }

    const missingVideos = videosForDownload.filter(
      (video) => !downloadableLinksByVideoId[Number(video.id)],
    );

    if (!missingVideos.length) {
      return;
    }

    let cancelled = false;

    const preloadLinks = async () => {
      try {
        setLoading(true);
        const results = await Promise.all(
          missingVideos.map(async (video) => {
            const res = await createVideofeedDownloadableLink(String(feedData.id), {
              selectedVideoIds: [Number(video.id)],
              ttlSeconds,
            });

            return {
              videoId: Number(video.id),
              url: String(res?.data?.publicUrl || '').trim(),
            };
          }),
        );

        if (cancelled) return;

        setDownloadableLinksByVideoId((prev) => {
          const next = { ...prev };
          results.forEach(({ videoId, url }) => {
            if (url) {
              next[videoId] = url;
            }
          });
          return next;
        });
      } catch (err) {
        console.error(err);
        enqueueSnackbar('Failed to preload downloadable links', {
          preventDuplicate: true,
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void preloadLinks();

    return () => {
      cancelled = true;
    };
  }, [showDownloadableSelector, feedData?.id, canCopyDownloadableLink, videosForDownload, downloadableLinksByVideoId, ttlSeconds]);

  const copyVideoUrl = () => {
    if (selectedVideoQuality?.url) {
      copyText(selectedVideoQuality.url)
        .then((copied) => {
          if (!copied) {
            throw new Error('Failed to copy');
          }
          enqueueSnackbar(`Copied video link`, {
            preventDuplicate: true,
            variant: 'success',
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
          });
          closeModal();
        })
        .catch((err) => {
          console.error('Failed to copy!', err);
          enqueueSnackbar(`Failed to copy`, {
            preventDuplicate: true,
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
          });
          closeModal();
        });
    }
  };

  const closeModal = () => {
    switchModal();
    setLoading(false);
  };

  const generateJson = async () => {
    try {
      const copied = await copyText(JSON.stringify(data));
      if (!copied) {
        throw new Error('Failed to copy');
      }
      enqueueSnackbar(`Copied video json`, {
        preventDuplicate: true,
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const copyDownloadableLink = async () => {
    if (!feedData?.id || !canCopyDownloadableLink) return;
    if (!selectedDownloadableVideoId) {
      enqueueSnackbar('Select an angle', {
        preventDuplicate: true,
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }

    try {
      const url = String(downloadableLinksByVideoId[selectedDownloadableVideoId] || '').trim();

      if (!url) {
        throw new Error('Downloadable link was not created');
      }

      const copied = await copyText(url);
      if (!copied) {
        showManualCopyPrompt(url);
        enqueueSnackbar('Clipboard blocked. Copy link from prompt.', {
          preventDuplicate: true,
          variant: 'warning',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        return;
      }
      enqueueSnackbar('Copied downloadable link', {
        preventDuplicate: true,
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      closeModal();
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to copy downloadable link', {
        preventDuplicate: true,
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={closeModal} loading={loading}>
      <div className={styles.modal}>
        {showDownloadableSelector ? (
          <div className={styles.actionBtns}>
            <label className={styles.expiryField}>
              <span>Download link expiry</span>
              <select
                value={ttlSeconds}
                onChange={(event) => setTtlSeconds(Number(event.target.value))}
              >
                <option value={24 * 60 * 60}>24 hours</option>
                <option value={7 * 24 * 60 * 60}>7 days</option>
                <option value={30 * 24 * 60 * 60}>30 days</option>
              </select>
            </label>
            {videosForDownload.map((video, index) => (
              <label
                key={video.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  color: 'white',
                }}
              >
                <input
                  type="radio"
                  checked={selectedDownloadableVideoId === Number(video.id)}
                  onChange={() => setSelectedDownloadableVideoId(Number(video.id))}
                />
                <span>{video.name || `Angle ${index + 1}`}</span>
                {!downloadableLinksByVideoId[Number(video.id)] && (
                  <span style={{ color: '#9ca3af', marginLeft: '6px' }}>
                    loading...
                  </span>
                )}
              </label>
            ))}
            <ContainedButton disabled={loading} onClick={copyDownloadableLink}>
              Copy Selected Downloadable Link
            </ContainedButton>
            <OutlinedButton
              disabled={loading}
              onClick={() => setShowDownloadableSelector(false)}
            >
              Back
            </OutlinedButton>
          </div>
        ) : (
          <div className={styles.actionBtns}>
            <ContainedButton disabled={loading} onClick={copyVideoUrl}>
              Copy Current Angle HLS
            </ContainedButton>
            <ContainedButton disabled={loading} onClick={generateJson}>
              Copy All HLS
            </ContainedButton>
            {canCopyDownloadableLink && (
              <ContainedButton
                disabled={loading}
                onClick={() => setShowDownloadableSelector(true)}
              >
                Copy Downloadable Link
              </ContainedButton>
            )}
            <OutlinedButton disabled={loading} onClick={closeModal}>
              Cancel
            </OutlinedButton>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CopyLinkModal;
